import {
	IonBackButton,
	IonButtons,
	IonChip,
	IonHeader,
	IonIcon,
	IonPage,
	IonProgressBar,
	IonRefresher,
	IonRefresherContent,
	IonText,
	IonTitle,
	IonToolbar,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { FriendCheckFilledCard } from "@/components/FriendCheck/FilledCard";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { Toolbar } from "@/components/Global/Toolbar";
import { backendService } from "@/lib/backend";
import { getBasePath } from "@/lib/backend/getBasePath";
import { useExtractedQuery } from "@/lib/backend/utils/hooks";
import { Clipboard } from "@/lib/capacitor/Clipboard";
import { Share } from "@/lib/capacitor/Share";
import { Toast } from "@/lib/capacitor/Toast";
import { CHOSEN_CORE_VALUES_LIMIT, FRIEND_CHECK_TARGET } from "@/lib/constants";
import { arrowLeftIcon } from "@/lib/icons/@heroicons/react/20/solid";
import {
	clipboardDocumentIcon,
	inboxIcon,
	questionMarkCircleIcon,
} from "@/lib/icons/@heroicons/react/24/outline";
import { useFeatureFlagsContext } from "@/lib/posthog/feature-flags/context";
import { queryOptions_canShare } from "@/lib/query/functions/@capacitor/share/canShare";
import { queryOptions_coreValuesChosen } from "@/lib/query/functions/core-values/chosen";
import { queryOptions_friendCheckSubmissions } from "@/lib/query/functions/friend-check/submissions";
import { useMe } from "@/lib/query/functions/user/me";
import { useMyProgression } from "@/lib/query/functions/user/progression";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";

export function FriendCheckDashboard() {
	const { t } = useTranslation();
	const presentModal = useSelectorModalStore.use.present();
	const [myProgression] = useMyProgression();
	const [me] = useMe();
	const featureFlags = useFeatureFlagsContext();
	const query = useQuery(queryOptions_friendCheckSubmissions());
	const queryCanShare = useQuery(queryOptions_canShare());
	const queryFriendCheckEnabled = useExtractedQuery({
		queryKey: ["friend-check", "enabled"],
		queryFn: (context) =>
			backendService.friendcheck.friendcheckControllerGetFriendCheckEnabled({
				signal: context.signal,
			}),
	});
	const queryFriendHash = useExtractedQuery({
		queryKey: ["friend-check", "friend-hash"],
		queryFn: (context) =>
			backendService.friendcheck.friendcheckControllerGetFriendHash({
				signal: context.signal,
				format: "text",
			}),
		enabled: !!queryFriendCheckEnabled.data,
	});

	const { data: coreValues } = useQuery(queryOptions_coreValuesChosen());
	const hasChosenCoreValues = coreValues?.length === CHOSEN_CORE_VALUES_LIMIT;

	const link =
		!queryFriendHash.data || !me?.friendId
			? undefined
			: getBasePath(
					Routes.SharedFriendCheck({
						id: me.friendId,
						hash: queryFriendHash.data,
					}),
					{ withScheme: false },
				);

	async function shareSheet() {
		await Share.share({
			dialogTitle: t("friend-check.share.title"),
			title: t("friend-check.share.title"),
			text: `${t("friend-check.share.text", {
				link,
				sender_name: me?.name,
			})}`,
		});
	}

	async function copyUrl() {
		await Clipboard.write({ url: link });
		await Toast.show({ text: t("copied-to-clipboard") });
	}

	return (
		<IonPage>
			<IonHeader>
				<Toolbar className="ion-p-2 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
					<IonTitle className="ion-text-brown-700 text-lg font-bold">
						{t("friend-check.name")}
					</IonTitle>

					<IonButtons slot="start">
						<IonBackButton
							data-attr="page.core-values.detail.back"
							data-touch-target
							defaultHref={Routes.Dashboard()}
							color="light"
							className="part-icon:m-0 part-icon:size-5 part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:bg-brown-300 part-native:p-1 part-native:text-xs part-native:text-brown-600 min-h-0"
							icon={arrowLeftIcon}
							text=""
						/>
					</IonButtons>
					<IonButtons slot="primary">
						<Button
							data-attr="page.app.friend-check.help"
							size="header"
							variant="header"
							onClick={() => presentModal("friendCheckExplainer")}
						>
							<IonIcon slot="icon-only" icon={questionMarkCircleIcon} />
						</Button>
					</IonButtons>
				</Toolbar>
			</IonHeader>

			<Content className="part-scroll:gap-4">
				<IonHeader collapse="condense" className="-mt-(--padding-top)">
					<IonToolbar className="ion-px-0">
						<IonTitle className="ion-text-brown-700 p-0 text-start text-3xl font-bold">
							{t("friend-check.name")}
						</IonTitle>
					</IonToolbar>
				</IonHeader>

				<IonRefresher
					slot="fixed"
					onIonRefresh={(event) => query.refetch().then(() => event.detail.complete())}
				>
					<IonRefresherContent />
				</IonRefresher>

				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-6">
						<div className="border-brown-300 flex flex-col gap-4 rounded-2xl border-2 border-solid p-4">
							<div className="flex w-full flex-col items-center gap-2">
								{(myProgression.friend_check === "NOT_STARTED" ||
									myProgression.friend_check === "STARTED") && (
									<IonIcon icon={inboxIcon} className="text-brown-400 mt-6 size-14" />
								)}

								{!query.data || query.data.length === 0 ? (
									<div className="text-brown-600 flex flex-col items-center text-center">
										<IonText className="font-bold">{t("friend-check.start-sharing.title")}</IonText>
										<IonText>{t("friend-check.start-sharing.text")}</IonText>
									</div>
								) : (
									<div className="text-brown-600 flex w-full flex-col items-center gap-4 text-center">
										{!!queryFriendCheckEnabled.data && (
											<>
												<IonChip
													disabled
													outline
													className="border-brown-300 mobile-sm:w-2/3 mobile-sm:min-w-32 mt-2 flex w-full max-w-full flex-row gap-2 border-2 px-3 py-1 opacity-100"
												>
													<IonProgressBar
														className="!ion-bg-brown-300 !ion-progress-bg-brown-400 h-2 rounded-full"
														value={query.data.length / FRIEND_CHECK_TARGET}
													/>
													<IonText className="text-brown-700 text-xs font-medium tabular-nums">
														{query.data.length > FRIEND_CHECK_TARGET
															? query.data.length
															: `${query.data.length}/${FRIEND_CHECK_TARGET}`}
													</IonText>
												</IonChip>
												{(query.data?.length ?? 0) < FRIEND_CHECK_TARGET && (
													<IonText>{t("friend-check.sharing.text")}</IonText>
												)}
											</>
										)}
										<div className="flex w-full flex-col gap-3 *:h-[4.5rem]">
											{query.data.map((friendCheckSubmission) => (
												<FriendCheckFilledCard
													key={friendCheckSubmission.id}
													data={friendCheckSubmission}
													withUnseen
													routerDirection="forward"
													routerLink={Routes.FriendCheckSubmission({
														id: friendCheckSubmission.id,
													})}
												/>
											))}
											{Array.from({
												length: Math.max(0, FRIEND_CHECK_TARGET - (query.data ?? []).length),
											}).map((_, index) => (
												<div
													key={index}
													className="border-brown-300 rounded-2xl border-2 border-dashed px-4 py-3"
												/>
											))}
										</div>
									</div>
								)}
							</div>
							{!!query.data && !!link && !hasChosenCoreValues && (
								<div className="flex w-full flex-col gap-2">
									{query.data.length > 0 && (
										<Button
											expand="block"
											routerLink={Routes.FriendCheckGenerate()}
											variant="secondary"
										>
											{t("friend-check.finish")}
										</Button>
									)}
									<Button
										data-attr="page.app.friend-check.action.copy"
										variant="secondary"
										expand="block"
										onClick={() => copyUrl()}
									>
										<IonText
											data-mask-text
											className="w-full truncate text-start leading-7 font-medium"
										>
											{link}
										</IonText>
										<IonIcon slot="end" icon={clipboardDocumentIcon} />
									</Button>
									{!!queryCanShare.data && featureFlags.canShare && (
										<Button
											data-attr="page.app.friend-check.action.share"
											expand="block"
											onClick={() => shareSheet()}
										>
											{t("friend-check.share-link")}
										</Button>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</Content>
		</IonPage>
	);
}
